import { useEffect, useState } from 'react'
import { Netzstreifen } from '../../components/Netzstreifen'
import { Link, useNavigate } from 'react-router-dom'

import { useHealth, useLogout, useMe } from '../../hooks/queries'
import { gemerkterOrt, startziel, type Startziel } from '../../lib/letzterChat'
import { useOnline, useOutboxAnzahl } from '../../hooks/useOutbox'
import {
  BackIcon,
  GlockeIcon,
  PersonIcon,
  PulseIcon,
  OpenSourceIcon,
  SchildIcon,
  SlidersIcon,
  TextSizeIcon,
} from '../../components/Icons'
import { bodenLage, hoehenVerlauf, sicherheitsabstaende, sichtVerworfen } from '../../lib/tastatur'
import { sendeVerlauf } from '../../lib/sendeprotokoll'
import { letzterVordergrund, sprungVerlauf, workerSpur, type Spurbericht } from '../../lib/tippziel'
import { DarstellungCard } from './DarstellungCard'
import { Listenfarben } from './Listenfarben'
import { KontoCard } from './KontoCard'
import { PushCard } from './PushCard'
import { Datenschutz } from './Datenschutz'
import { OpenSourceCard } from './OpenSourceCard'
import { Kategorien, type Kategorie } from './Kategorien'
import { istAppleMitFinger } from '../../lib/geraet'
import { useTexte, WORTLAUT } from '../../texte'

export function SettingsPage() {
  const t = useTexte()
  const me = useMe()
  const logout = useLogout()
  const navigate = useNavigate()

  const kategorien: Kategorie[] = [
    {
      schluessel: 'konto',
      titel: t.einstellungen.konto,
      symbol: <PersonIcon size={19} />,
      inhalt: () => (
        <>
        <div className="card card--seite">
          <h2 className="card__titel">{me.data?.display_name}</h2>
          <p className="card__lead">{me.data?.email}</p>

          <p className="leise konto__rolle">
            {me.data?.may_manage_people
              ? t.konto.darfAlles
              : me.data?.may_create_trips
                ? t.konto.darfReisen
                : t.konto.darfMitlesen}
          </p>

          <button
            className="button button--outline"
            type="button"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => navigate('/login', { replace: true }),
              })
            }
          >
            {t.konto.abmelden}
          </button>
        </div>

        {/* Was jemand an seinem Konto selbst ändern darf (#40, #45). Unter der
            Übersicht und nicht darüber: wer hierher kommt, will meistens nur
            nachsehen, wer er gerade ist. */}
        <KontoCard />
        </>
      ),
    },
    {
      schluessel: 'benachrichtigungen',
      titel: t.einstellungen.benachrichtigungen,
      symbol: <GlockeIcon size={19} />,
      inhalt: () => (
        <>
          <PushCard />
          {/* Der Hinweis gehört hierher und nicht zum Konto: ohne diesen
              Schritt gibt es auf iOS überhaupt keine Benachrichtigungen --
              Apple gibt die Push-Schnittstelle nur Web-Apps auf dem
              Home-Bildschirm, nicht Seiten im Safari-Tab. */}
          {istAppleMitFinger() && !isStandalone() && (
            <div className="card card--seite card--dicht">
              <h2 className="card__titel">{t.installieren.titel}</h2>
              <p className="card__lead card__lead--dicht">
                {t.installieren.lead}
              </p>
            </div>
          )}
        </>
      ),
    },
    {
      schluessel: 'darstellung',
      titel: t.einstellungen.darstellung,
      symbol: <TextSizeIcon size={19} />,
      inhalt: () => (
        <>
          <DarstellungCard />
          {/* **Bei jedem, nicht nur bei der Verwaltung** (#54): sie regelt
              nichts an einer Reise, sondern etwas an den Augen dessen, der
              davorsitzt -- dieselbe Begründung wie bei der Schriftgröße. */}
          <Listenfarben />
        </>
      ),
    },
    /*
     * **Datenschutz -- fuer jedes Konto** (#11).
     *
     * Hier stand eine Bedingung auf `may_manage_people`, und sie war
     * ausdruecklich eine Stufe: die Kategorie entsteht, wird am Geraet
     * angesehen, und dann wird sie aufgemacht. Genau das ist jetzt geschehen
     * -- aus dem Betrieb: „#11 --> fuer alle sichtbar."
     *
     * **Geschrieben war sie immer fuer Mitlesende.** Dort steht, wessen
     * IP-Adresse wohin geht und wer verantwortlich ist; ein Text, den nur die
     * Verwaltung sieht, erfuellt seinen Zweck nicht. Im Server ist `operator`
     * dafuer vor das `if (!$istVerwaltung)` gewandert.
     *
     * Sie steht **vor der Diagnose** und ist nicht umgezogen -- dort stand sie
     * von Anfang an, damit das Aufmachen keine zweite Aenderung wird. Vor der
     * Diagnose, weil die drei Diagnosekarten Nachschlagewerk fuer den
     * Fehlerfall sind und keine Auskunft; und weil „Administration"
     * unmittelbar auf die Diagnose folgt -- das ist eine Ansage aus dem
     * Betrieb und in `tools/pruefstand/einstellungen.mjs` gemessen.
     */
    {
      schluessel: 'datenschutz',
      titel: t.einstellungen.datenschutz,
      symbol: <SchildIcon size={19} />,
      inhalt: () => <Datenschutz />,
    },
    /*
     * Which open-source projects this uses -- **for everyone** (#61).
     *
     * Unlike the privacy page this one carries no condition: naming these is
     * an obligation towards the people whose work ships here, and an
     * obligation that only an administrator can see is none.
     */
    {
      schluessel: 'opensource',
      titel: t.einstellungen.openSource,
      symbol: <OpenSourceIcon size={19} />,
      inhalt: () => <OpenSourceCard />,
    },
    {
      schluessel: 'diagnose',
      titel: t.einstellungen.diagnose,
      symbol: <PulseIcon size={19} />,
      inhalt: () => (
        <>
          {/* Zuletzt, und das mit Absicht: die drei Karten sind Nachschlagewerk
              für den Fehlerfall, nicht Bedienung. Wer etwas einstellen will,
              soll nicht erst an Messwerten vorbei -- deshalb stehen sie jetzt
              hinter einer eigenen Kategorie statt am Ende einer langen Seite. */}
          {/*
            **Der Stand steht oben, nicht unten** (aus dem Betrieb: „die Infos
            sind oft wichtiger als die von weiter unten").

            Er beantwortet die Frage, die vor allen anderen kommt: **welche
            Fassung läuft hier überhaupt?** Ohne sie ist jede Messung darunter
            wertlos -- ein Bildschirmfoto vom Mitschrieb hat in dieser Runde
            schon einmal eine Runde gekostet, weil niemand wusste, aus welchem
            Stand es kam. Wer die Diagnose aufmacht, soll es als Erstes sehen
            und nicht erst dorthin scrollen.
          */}
          <Stand />
          <Netzlage />
          <Sprungmitschrieb />
          <Tastaturlage />
        </>
      ),
    },
    /*
     * **Und ganz unten die Administration -- als Zeile, nicht als Knopf in der
     * Kontokarte.**
     *
     * Aus dem Betrieb: „das Menü Verwaltung über Konto aufzurufen ist aber
     * nicht intuitiv. besser wäre das auch aus der linken Seitenleiste
     * aufzurufen." Genau hier sucht man sie -- in der Liste, in der alles
     * andere auch steht. Der Knopf in der Kontokarte ist ersatzlos weg: zwei
     * Wege zu derselben Seite sind vor allem eine Frage danach, ob sie
     * dasselbe tun.
     *
     * Sie zeigt rechts nichts, sondern **führt woandershin**: dahinter liegt
     * eine eigene Liste mit sieben eigenen Kategorien. Deshalb `ziel` und kein
     * `inhalt`, und deshalb trägt die Zeile das Zeichen, das das ansagt.
     *
     * **Wer nicht darf, bekommt sie gar nicht erst.** Die Regel steht ohnehin
     * im Server (`requireAdmin`); hier wird sie nur nicht angeboten.
     */
    ...(me.data?.may_manage_people
      ? [
          {
            schluessel: 'administration',
            titel: t.einstellungen.administration,
            symbol: <SlidersIcon size={19} />,
            ziel: '/settings/administration',
          } satisfies Kategorie,
        ]
      : []),
    // „Betrieb" (#62): die technische Seite, nur für den Supervisor -- und
    // der Server sagt, wer das ist (`may_operate`), nicht die Rolle.
    ...(me.data?.may_operate
      ? [
          {
            schluessel: 'betrieb',
            titel: t.einstellungen.betrieb,
            symbol: <PulseIcon size={19} />,
            ziel: '/settings/betrieb',
          } satisfies Kategorie,
        ]
      : []),
  ]

  return (
    <>
      <Netzstreifen />
      <Kategorien
        titel={t.einstellungen.titel}
        zurueck={
          <Link className="icon-button" to="/" aria-label={t.einstellungen.zurueck}>
            <BackIcon />
          </Link>
        }
        kategorien={kategorien}
      />
    </>
  )
}

/**
 * Welcher Stand hier läuft -- und welcher auf dem Server.
 *
 * Zwei Angaben statt einer, weil genau ihr Auseinanderfallen der Fehler war,
 * den sonst niemand sieht: ein Browser, der eine alte Fassung aus dem
 * Zwischenspeicher fährt, sieht vollkommen gesund aus.
 *
 * Aus demselben Grund steht hier auch, **wo die Anwendung beim nächsten Mal
 * aufmacht**. Aus dem Betrieb: am iPad kam der verlassene Chat, am iPhone
 * immer irgendein Chat — und ob dort nichts gemerkt war oder etwas Falsches,
 * ließ sich von außen nicht sehen. Eine Zeile beendet das Raten; sie steht
 * neben den Ständen, weil beides zur selben Frage gehört, wenn zwei Geräte
 * sich verschieden verhalten.
 */
function Stand() {
  const t = useTexte()
  const health = useHealth(true)

  /*
   * **Eine eigene Karte statt einer Fußzeile.**
   *
   * Das hier stand als kleine graue Zeile ganz unten unter drei Messkarten --
   * zentriert, zwölf Punkte, wie ein Impressum. Aus dem Betrieb kam, was
   * daran falsch war: die Angabe ist **oft wichtiger als das darüber**. Sie
   * sagt, welche Fassung gerade läuft; ohne sie ist jede Messung darunter
   * nicht einzuordnen.
   *
   * Deshalb steht sie jetzt oben, in derselben Form wie alles andere hier --
   * mit Überschrift, linksbündig, lesbar groß. Ein Bildschirmfoto davon ist
   * die Antwort auf die erste Frage, die bei jeder Meldung kommt.
   */
  return (
    <div className="card card--seite" data-schluessel="stand">
      <h2 className="card__titel">{t.stand.titel}</h2>
      <p className="card__lead card__lead--knapp">
        {t.stand.lead}
      </p>
      <p className="stand__zeile">
        {t.stand.zeile(health.data?.version ?? '—', __BUILD__, health.data?.build ?? '—')}
      </p>
      <p className="leise leise--eng">
        {t.stand.anzeige(anzeigeArt(), herkunft())} · {t.stand.gelesen(nenne(startziel()))} ·{' '}
        {t.stand.gemerkt(nenne(gemerkterOrt()))}
      </p>
    </div>
  )
}

/**
 * Womit die Seite geladen wurde — und ob der Browser das als sicher ansieht.
 *
 * Aus dem Betrieb: in der Web-App meldet iOS „Die Verbindung zu dieser Website
 * ist nicht sicher" mit einem leeren Zertifikat, in Safari dagegen das gültige.
 * Von außen sind dafür zwei ganz verschiedene Ursachen möglich — die App läuft
 * tatsächlich über `http`, weil das Symbol einmal von einer unverschlüsselten
 * Adresse angelegt wurde, oder jenes Blatt sagt in einer Web-App schlicht
 * nichts Sinnvolles. Der Unterschied entscheidet, ob etwas zu reparieren ist,
 * und die Seite selbst weiß ihn.
 */
function herkunft(): string {
  // Der Zusatz kommt aus `de.ts`; ohne Komponente gibt es keinen Haken, also
  // wird er hier unmittelbar geholt -- eine der wenigen Stellen, an denen das
  // richtig ist, und `tools/pruefstand/texte.ts` führt sie als Ausnahme.
  return `${location.protocol}${window.isSecureContext ? '' : ` (${WORTLAUT.stand.unsicher})`}`
}

/** „Chat /t/7", „Übersicht" oder „nichts" -- in einer Zeile lesbar. */
function nenne(ziel: Startziel): string {
  if (ziel === null) return WORTLAUT.stand.keinZiel
  return ziel.art === 'liste' ? WORTLAUT.stand.uebersicht : WORTLAUT.stand.chat(ziel.pfad)
}

/**
 * Was das Gerät über seine Verbindung denkt -- und was noch aussteht.
 *
 * Diese Zeile beendet ein Ratespiel. Aus dem Betrieb: „Flugmodus an, aber kein
 * Hinweis" — und in Wahrheit war das WLAN weiter an, denn der Flugmodus
 * schaltet es auf dem iPhone nicht zwingend ab. Das Flugzeugsymbol sagt eben
 * nichts darüber, ob etwas durchgeht.
 *
 * Deshalb stehen hier **drei Angaben nebeneinander**: was der Browser meldet
 * (`navigator.onLine`), was zuletzt tatsächlich passiert ist (`netzStand()`)
 * und wie viele Nachrichten warten. Fallen die ersten beiden auseinander,
 * hängt das Gerät an einem Netz, durch das nichts hindurchgeht.
 */
/**
 * Was beim letzten Tipp auf eine Benachrichtigung geschah.
 *
 * **Weil dreimal Raten genug ist.** Aus dem Betrieb, zum zweiten Mal: „der
 * Absprung aus der Push-Nachricht in die Anfragen funktioniert nicht." Im
 * Labor sind beide Wege grün — das heißt nicht, dass sie gehen, sondern dass
 * das Labor die falsche Frage stellt. Dieselbe Lage wie beim Sendeknopf, und
 * dieselbe Antwort: kein weiterer Anlauf ins Blaue, sondern ein Satz vom
 * Gerät.
 *
 * Steht hier nichts, ist seit dem Start der Anwendung kein Sprung versucht
 * worden — und das ist selbst eine Auskunft: dann hat der Worker gar nichts
 * geschickt und die Nachfrage beim Aufbau nichts vorgefunden.
 */
function Sprungmitschrieb() {
  const t = useTexte()
  // Neu eingelesen bei jedem Zeichnen: der Mitschrieb liegt außerhalb von
  // React und meldet sich nicht. Wer herkommt, wechselt ohnehin die Seite.
  const zeilen = sprungVerlauf()
  const vorn = letzterVordergrund()

  /*
   * **Die Spur wird neu gelesen, wenn die App nach vorn kommt — und auf
   * Knopfdruck.**
   *
   * Sie wurde einmal beim Aufbau gelesen und danach nie wieder. Aus dem
   * Betrieb kamen daraufhin zwei Bilder derselben Karte, elf Sekunden und
   * anderthalb Minuten auseinander: das erste sagte „nichts aufgeschrieben",
   * das zweite zeigte sechs Zeilen — darunter ältere als das erste Bild. Nicht
   * die Spur war alt, **die Anzeige war es.** Wer die Karte offen hat und
   * daneben eine Benachrichtigung antippt, kommt genau so zurück.
   */
  const [spur, setSpur] = useState<Spurbericht | null>(null)
  const [zaehler, setZaehler] = useState(0)
  useEffect(() => {
    let gilt = true
    setSpur(null)
    void workerSpur().then((s) => {
      if (gilt) setSpur(s)
    })
    return () => {
      gilt = false
    }
  }, [zaehler])
  useEffect(() => {
    const nachVorn = () => {
      if (document.visibilityState === 'visible') setZaehler((n) => n + 1)
    }
    document.addEventListener('visibilitychange', nachVorn)
    return () => document.removeEventListener('visibilitychange', nachVorn)
  }, [])

  return (
    /*
      **Die Karte traegt einen Schluessel, und der Prueflauf haengt daran.**
      Ihre Ueberschrift steht seit `0.16.0` in `texte/de.ts` und darf sich
      aendern; ein Lauf, der sie festhaelt, wird bei genau dieser Aenderung rot
      und hat nichts gefunden.
    */
    <div className="card card--seite" data-schluessel="spruenge">
      <h2 className="card__titel">{t.spruenge.titel}</h2>
      <p className="card__lead card__lead--knapp">
        {t.spruenge.lead}
      </p>

      {/*
        **Der Stand steht an der Karte selbst.** Aus dem Betrieb: ein Bild
        dieser Karte kam an, und die entscheidende Hälfte fehlte darin -- es
        war die Fassung von davor, und das sah man nur an einem Satz Prosa.
        Ein Mitschrieb, der nicht sagt, aus welchem Stand er kommt, kostet
        eine ganze Runde. Er steht zwar auch unter „Stand", aber das ist eine
        andere Karte und damit ein zweites Bild.
      */}
      <p className="leise leise--klein leise--absatz">
        {t.spruenge.oberflaeche} <code>{__BUILD__}</code>
      </p>

      <h3 className="diagnose__titel">{t.spruenge.imHintergrund}</h3>
      {/*
        **Drei Ausgänge, drei Sätze.** „Er hat nichts aufgeschrieben" stand
        hier auch dann, wenn er in Wahrheit voll war und bloß nicht
        rechtzeitig antwortete -- und genau das ist auf dem iPhone der
        Normalfall, weil ein angehaltener Worker erst anlaufen muss. Ein
        Werkzeug, das überzeugend das Falsche sagt, ist schlimmer als keines.
      */}
      {spur === null || spur.lage !== 'da' ? (
        <p className="leise leise--absatz">
          {spur === null
            ? t.spruenge.wirdGelesen
            : spur.lage === 'kein-worker'
              ? t.spruenge.keinWorker
              : spur.lage === 'keine-antwort'
                ? t.spruenge.keineAntwort
                : t.spruenge.nichts}
        </p>
      ) : (
        <ul className="hinweisliste hinweisliste--absatz">
          {spur.zeilen.map((zeile, i) => (
            <li key={i}>
              <code>{zeile.zeit}</code> {zeile.was}
            </li>
          ))}
        </ul>
      )}

      <button
        className="button button--outline button--abstand"
        type="button"
        onClick={() => setZaehler((n) => n + 1)}
      >
        {t.spruenge.neuLesen}
      </button>

      <h3 className="diagnose__titel">{t.spruenge.inDerApp}</h3>
      {/*
        **Wann die App zuletzt nach vorn kam -- und warum das hier oben steht.**

        Aus dem Betrieb kam eine Spur mit acht Push-Nachrichten und keinem
        einzigen „Angetippt". Damit liess sich die eine Frage nicht
        beantworten, fuer die es diese Karte gibt: hat niemand getippt, oder
        kam der Tipp nicht an? **Beides sah gleich aus.**

        Eine App kommt nicht von selbst nach vorn. Steht hier ein Zeitpunkt
        wenige Sekunden nach einer Push-Nachricht und oben kein „Angetippt",
        dann hat jemand getippt und der Worker hat es nicht erfahren.

        Neu gelesen wird es mit derselben Runde wie die Spur darueber: die
        Karte zeichnet neu, wenn die App nach vorn kommt.
      */}
      <p data-schluessel="vordergrund" className="leise leise--absatz">
        {vorn === null ? t.spruenge.nieVorn : t.spruenge.zuletztVorn(vorn)}
      </p>
      {zeilen.length === 0 ? (
        <p className="leise leise--eng">
          {t.spruenge.nichtsInDerApp}
        </p>
      ) : (
        <ul className="hinweisliste">
          {zeilen.map((zeile, i) => (
            <li key={i}>
              <code>{zeile.zeit}</code> {zeile.was}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Netzlage() {
  const t = useTexte()
  const online = useOnline()
  const wartend = useOutboxAnzahl()
  const [browser, setBrowser] = useState(() => navigator.onLine)

  useEffect(() => {
    const pruefen = () => setBrowser(navigator.onLine)
    window.addEventListener('online', pruefen)
    window.addEventListener('offline', pruefen)
    return () => {
      window.removeEventListener('online', pruefen)
      window.removeEventListener('offline', pruefen)
    }
  }, [])

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.verbindung.titel}</h2>
      {/*
        **„Es geht gerade etwas durch" stand hier und war eine Frage statt einer
        Auskunft.** Aus dem Betrieb: „Was bedeutet das? online,
        Datenübertragung läuft?" -- beides ließ sich hineinlesen, und keines
        stimmte. Gemeint ist weder eine laufende Übertragung noch das, was der
        Browser über das Netz behauptet, sondern: **die letzte Anfrage ist
        angekommen** (`netzStand()` in `api/client.ts`). Genau das steht jetzt
        da, und dahinter, was daraus für den Benutzer folgt.
      */}
      <p className="card__lead card__lead--knapp">
        {online ? t.verbindung.online : t.verbindung.offline}
      </p>

      <ul className="hinweisliste">
        <li>
          {t.verbindung.erreicht}: {online ? t.verbindung.ja : t.verbindung.nein}
        </li>
        <li>
          {t.verbindung.browserMeldet}: {browser ? t.verbindung.ja : t.verbindung.nein}
        </li>
        <li>
          {t.verbindung.wartende}: <strong>{wartend}</strong>
        </li>
      </ul>

      {browser && !online && (
        <p className="notice notice--oben" role="alert">
          {t.verbindung.taubesNetz}
        </p>
      )}

      {/* Eine unverschlüsselte Verbindung fällt sonst nicht auf, richtet aber
          still Schaden an: ohne sie gibt es keine Benachrichtigungen, und was
          hier steht, liest jeder im selben Netz mit. Auf dem Telefon entsteht
          das meist so, dass das Symbol einmal von einer `http`-Adresse aus
          angelegt wurde -- die Web-App startet dann für immer dort. */}
      {!window.isSecureContext && (
        <p className="notice notice--oben" role="alert">
          {t.verbindung.unverschluesselt(location.protocol)}
        </p>
      )}
    </div>
  )
}

/**
 * Was das Gerät tut, wenn die Tastatur aufgeht.
 *
 * Dieselbe Absicht wie bei *Verbindung* darüber: eine Frage beenden, die von
 * außen nicht zu beantworten ist. Ob die Eingabezeile unter der Tastatur
 * liegt, hängt an vier Zahlen, und keine davon ist zu sehen — der Prüfstand
 * hier hat keine iOS-Tastatur, und ein Bildschirmfoto allein sagt nicht, ob
 * die Fläche verschoben oder der Abstand falsch gemessen ist.
 *
 * Deshalb ein Feld zum Hineintippen und die Zahlen darunter, die sich dabei
 * bewegen. Fehlt der Abstand, während die Tastatur offen ist, misst der
 * Browser anders als angenommen; ist der Versatz groß, schiebt das Gerät die
 * Fläche.
 */
function Tastaturlage() {
  const t = useTexte()
  const [werte, setWerte] = useState(lies)
  /**
   * Einmal beim Einhängen, und nie wieder.
   *
   * **Ein Messgerät, das den Messwert stört, ist keins.** Beide Messungen
   * hängen einen `position: fixed`-Kasten ins Dokument und nehmen ihn wieder
   * heraus -- einer davon mit `height: 100dvh`. Sie standen in `lies()`, und
   * das läuft bei **jedem** Ereignis der sichtbaren Fläche, also durch die
   * ganze Tastaturanimation hindurch. Beide Werte ändern sich ohnehin nicht,
   * solange das Gerät nicht gedreht wird.
   */
  const [fest] = useState(() => ({ dvh: `${dvhMessen()}`, raender: raenderMessen() }))
  /**
   * Der Mitschrieb wird **einmal beim Einhängen** geholt und danach nicht mehr.
   *
   * Sonst schöbe jedes Ereignis dieser Seite die Einträge weg, um die es geht
   * -- die aus dem Chat, von *vor* dem Wechsel hierher.
   */
  const [verlauf] = useState(hoehenVerlauf)
  /** Derselbe Grund wie beim Mitschrieb der Höhen: einmal beim Einhängen. */
  const [senden] = useState(sendeVerlauf)

  useEffect(() => {
    const sicht = window.visualViewport
    const auffrischen = () => setWerte(lies())

    // **Einmal nach dem Einhängen.** `useState(lies)` läuft beim Zeichnen, und
    // da steht `.shell` noch nicht im Dokument -- die Zeile bliebe bei „—"
    // stehen, bis sich zufällig etwas bewegt.
    auffrischen()

    sicht?.addEventListener('resize', auffrischen)
    sicht?.addEventListener('scroll', auffrischen)
    window.addEventListener('resize', auffrischen)
    return () => {
      sicht?.removeEventListener('resize', auffrischen)
      sicht?.removeEventListener('scroll', auffrischen)
      window.removeEventListener('resize', auffrischen)
    }
  }, [])

  // Wo der Browser die Tastatur selbst wegrechnet, gibt es hier nichts zu
  // sehen und nichts zu erklären.
  if (!window.visualViewport) return null

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.tastatur.titel}</h2>
      <p className="card__lead card__lead--knapp">
        {t.tastatur.lead}
      </p>

      <input
        className="field__input field__input--abstand"
        type="text"
        placeholder={t.tastatur.feld}
        aria-label={t.tastatur.feldName}
      />

      <ul className="hinweisliste">
        <li>
          {t.tastatur.hoeheApp}: <strong>{werte.shell}</strong>{' '}
          {t.tastatur.vonFenster(werte.fenster)}
        </li>
        <li>
          {t.tastatur.abstand}: <strong>{werte.abstand}</strong>
        </li>
        <li>{t.tastatur.fensterhoehe}: {werte.fenster}</li>
        <li>{t.tastatur.sichtbareHoehe}: {werte.sicht}</li>
        <li>{t.tastatur.versatz}: {werte.versatz}</li>
        <li>{t.tastatur.seitenversatz}: {werte.seite}</li>
        <li>{t.tastatur.gesetzteHoehe}: {werte.gesetzt}</li>
        <li>{t.tastatur.dvh}: {fest.dvh}</li>
        <li>{t.tastatur.bildschirm}: {werte.schirm}</li>
        <li>{t.tastatur.fensterbreite}: {werte.breite}</li>
        <li>{t.tastatur.alsApp}: {werte.alsApp}</li>
        <li>
          {t.tastatur.mindesthoehe}: <strong>{werte.boden}</strong>
        </li>
        <li>{t.tastatur.raender}: {fest.raender}</li>
      </ul>

      {/* **Der Mitschrieb, und er ist der eigentliche Zweck dieser Karte.**
          Aus dem Betrieb kam ein Bildschirmfoto mit dem Streifen und eine
          Minute später diese Karte mit „874 von 874". Die Messung von `100dvh`
          hängt einen Kasten ins Dokument, erzwingt damit einen Neuaufbau und
          räumt den Zustand weg, den sie zeigen soll. Hier steht, was *vorher*
          war -- aufgeschrieben aus Werten, die nichts erzwingen. */}
      <p className="card__lead card__lead--zwischen">
        {t.tastatur.mitschrieb}
      </p>
      <ul className="hinweisliste hinweisliste--klein">
        {verlauf.map((stand, i) => (
          <li key={i}>
            <strong>{stand.fenster}</strong> / {stand.sicht}
            {' / '}{stand.gesetzt === null ? '—' : stand.gesetzt}
            {' / '}{stand.shell === null ? '—' : <strong>{stand.shell}</strong>}
            {' / '}{stand.zeile === null ? '—' : <strong>{stand.zeile}</strong>}
            {' · '}<strong>{stand.abstand}</strong>
            {' · '}<strong>{stand.seite}</strong>↕<strong>{stand.versatz}</strong>
            {' · '}{stand.zeit} · {stand.anlass} · {stand.pfad}
          </li>
        ))}
      </ul>

      {/* **Der Sendeknopf, aufgeschrieben** (#28). Aus dem Betrieb: „Senden
          über den Sendeknopf: geht manchmal, manchmal nicht." Hier steht, ob
          der Druck überhaupt angekommen ist -- verschluckt iOS den Tipp, steht
          gar nichts da -- und wo `submit()` hängengeblieben ist.

          **Seit #4 steht hier auch der Videoweg**, aus demselben Grund: „es
          läuft einmal auf 100 % und bleibt dann stehen." Der Balken sagt nur,
          wie viele Bytes draußen sind; welche Kodierung hereinkam, welche
          Zielgröße gewählt wurde und an welchem der drei Schritte danach es
          stehen blieb, steht nur hier. Deshalb nennt die Überschrift jetzt
          beides. */}
      <p className="card__lead card__lead--zwischen">
        {t.tastatur.senden}
      </p>
      {senden.length === 0 ? (
        <p className="leise leise--klein leise--eng">
          {t.tastatur.sendenLeer}
        </p>
      ) : (
        <ul className="hinweisliste hinweisliste--klein">
          {senden.map((schritt, i) => (
            <li key={i}>
              {schritt.zeit} · {schritt.was}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Was `100dvh` auf diesem Gerät wirklich ergibt.
 *
 * **Ausgemessen und nicht erfragt.** Es gibt keine Schnittstelle, die eine
 * CSS-Einheit ausrechnet; also bekommt ein unsichtbarer Kasten die Einheit
 * und wird vermessen. Genau diese Zahl war der blinde Fleck: `dvh` stand für
 * das Sichtbare und war es auf zwei Geräten nicht.
 */
function dvhMessen(): number {
  const kasten = document.createElement('div')
  kasten.style.cssText
    = 'position:fixed;top:0;left:0;width:1px;height:100dvh;visibility:hidden;pointer-events:none'
  document.body.appendChild(kasten)
  const hoch = Math.round(kasten.getBoundingClientRect().height)
  kasten.remove()
  return hoch
}

/**
 * Die vier Sicherheitsabstände — gemessen wird in `lib/tastatur.ts`.
 *
 * **Dort und nicht hier**, seit die Mindesthöhe am oberen Abstand hängt: die
 * Messung entscheidet etwas, und eine zweite Kopie davon liefe früher oder
 * später auseinander. Hier steht nur noch, wie sie dasteht.
 */
function raenderMessen(): string {
  const r = sicherheitsabstaende()
  return [r.oben, r.rechts, r.unten, r.links].join(' / ')
}

function lies() {
  const sicht = window.visualViewport
  const shell = document.querySelector('.shell')
  return {
    fenster: Math.round(window.innerHeight),
    // **Der Rohwert bleibt stehen, der Befund kommt dazu.** Genau diese Zahl
    // hat den Müllwert −33554432 gefunden -- sie zu glätten hieße, das
    // Messgerät zu verstellen. Ob sie verrechnet wird, entscheidet die
    // Verwerfungsgrenze in `tastatur.ts`; hier steht nur, was sie entschied.
    sicht: sicht
      ? `${Math.round(sicht.height)}${sichtVerworfen() ? ' (verworfen)' : ''}`
      : '0',
    versatz: sicht ? Math.round(sicht.offsetTop) : 0,
    seite: Math.round(window.scrollY),
    abstand:
      getComputedStyle(document.documentElement).getPropertyValue('--tastatur-hoehe').trim() ||
      '0px',
    // **Die Zahlen, die beim letzten Mal gefehlt haben.** Der Streifen unter
    // der Eingabezeile war aus einem Bildschirmfoto zu rechnen, weil das Gerät
    // ihn nicht selbst nennen konnte. Jetzt nennt es ihn.
    shell: shell === null ? '—' : `${Math.round(shell.getBoundingClientRect().height)}`,
    gesetzt:
      getComputedStyle(document.documentElement).getPropertyValue('--app-hoehe').trim()
      || WORTLAUT.tastatur.nichtGesetzt,
    schirm: `${Math.round(window.screen.width)} × ${Math.round(window.screen.height)}`,
    // **Die beiden Zahlen, an denen die Mindesthöhe hängt.** Ohne sie war aus der
    // Karte nicht zu sehen, *warum* er nicht griff -- und zwei Anläufe gingen
    // daran vorbei.
    ...(() => {
      const l = bodenLage()
      return {
        alsApp: l.alsApp ? 'ja' : 'nein',
        breite: `${l.breite}`,
        boden: l.boden === null ? WORTLAUT.tastatur.greiftNicht : `${l.boden}`,
      }
    })(),
  }
}



/**
 * Läuft das hier als Web-App vom Home-Bildschirm?
 *
 * Auf iOS zählt `navigator.standalone` und sonst nichts. Der Anzeigemodus
 * allein reicht nicht: In-App-Browser können `display-mode: standalone`
 * melden, weil aus Sicht der Seite keine Leisten da sind — und dann bliebe
 * der Hinweis zum Installieren ausgerechnet dem verborgen, der ihn braucht.
 */
function isStandalone(): boolean {
  const alsApp = (navigator as { standalone?: boolean }).standalone
  if (alsApp !== undefined) return alsApp
  return window.matchMedia('(display-mode: standalone)').matches
}

/**
 * Web-App oder Browser — und wenn Browser, ob eines mit Leisten.
 *
 * Aus dem Betrieb, und es hat eine Runde gekostet: ein Bildschirmfoto der
 * Standzeile kam aus einem In-App-Browser, während am Gerät sonst die Web-App
 * benutzt wird. Beides sieht innen gleich aus, hat unter iOS aber **je einen
 * eigenen Speicher** — was in dem einen gemerkt wurde, gibt es im anderen
 * nicht. Wer das nicht weiß, hält es für einen Fehler der Anwendung.
 *
 * `navigator.standalone` ist dabei die verlässlichere Auskunft: manche
 * In-App-Browser melden `display-mode: standalone` mit, weil aus Sicht der
 * Seite keine Leisten da sind. Fallen die beiden auseinander, steht genau das
 * da — geraten wird nicht.
 */
function anzeigeArt(): string {
  const alsApp = (navigator as { standalone?: boolean }).standalone
  const ohneLeisten = window.matchMedia('(display-mode: standalone)').matches

  if (alsApp === true) return WORTLAUT.stand.app
  if (alsApp === false) {
    return ohneLeisten ? WORTLAUT.stand.browserOhneLeisten : WORTLAUT.stand.browser
  }
  // Außerhalb von iOS gibt es das Feld nicht; dort genügt der Anzeigemodus.
  return ohneLeisten ? WORTLAUT.stand.app : WORTLAUT.stand.browser
}
